import { Badge } from "@/components/badge";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { cn } from "@/lib/utils";
import type { NodeData } from "@repo/shared/workflow";
import { useNodesData } from "@xyflow/react";
import {
  BoxIcon,
  FileTextIcon,
  LayersIcon,
  PlayIcon,
  WandIcon,
} from "lucide-react";
import type { FC } from "react";
import {
  type NodeDisplay,
  type RFNode,
  useEditorNodes,
  useEditorState,
} from "../state";
import { ActionNodeEditor } from "./ActionNodeEditor";
import { DocumentNodeEditor } from "./DocumentNodeEditor";
import { EmailNodeEditor } from "./EmailNodeEditor";
import { ExtractionNodeEditor } from "./ExtractionNodeEditor";
import { GroupNodeEditor } from "./GroupNodeEditor";

interface NodeEditorProps {
  workflowId: string;
  node: NodeDisplay;
  className?: string;
  // nodeNames: Record<string, string>;
  onChange: (updatedNode: NodeData) => void;
}

export const NodeEditor: FC<NodeEditorProps> = ({
  workflowId,
  node: nodeDisplay,
  className,
  // nodeNames,
  onChange,
}) => {
  const { displayName, isInternalNode, node, displayDependencyIds } =
    nodeDisplay;
  const depData = useNodesData<RFNode>(displayDependencyIds);
  const editorNodes = useEditorNodes();

  const updateNodeData = (key: string, value: unknown) => {
    if (!node) return;
    onChange({
      ...node,
      [key]: value,
    });
  };

  // TODO: loading state
  if (!node) return <div>Loading... {nodeDisplay.node.id}</div>;

  const renderNodeTypeEditor = () => {
    if (!node) return null;

    if (node.subType === "document")
      return <DocumentNodeEditor node={node} onChange={updateNodeData} />;
    if (node.subType === "extraction")
      return <ExtractionNodeEditor node={node} onChange={updateNodeData} />;
    if (node.subType === "email")
      return <EmailNodeEditor node={node} onChange={updateNodeData} />;
    if (node.type === "action")
      return <ActionNodeEditor node={node} onChange={updateNodeData} />;
    if (node.type === "group")
      return <GroupNodeEditor node={node} onChange={updateNodeData} />;
    return null;
  };

  const getNodeTypeIcon = () => {
    if (!node) return null;

    let IconComponent: React.ElementType;
    let iconColor = "";
    let label = "";

    switch (node.type) {
      case "entity":
        IconComponent = node.subType === "document" ? FileTextIcon : LayersIcon;
        iconColor = "text-blue-600";
        label = `${node.subType} Entity`;
        break;
      case "transform":
        IconComponent = WandIcon;
        iconColor = "text-green-600";
        label = `${node.subType} Transform`;
        break;
      case "action":
        IconComponent = PlayIcon;
        iconColor = "text-purple-600";
        label = `${node.subType} Action`;
        break;
      default:
        IconComponent = BoxIcon;
        iconColor = "text-gray-600";
        label = "Unknown Node";
    }

    return (
      <div className="flex items-center gap-2">
        <IconComponent className={`h-5 w-5 ${iconColor}`} />
      </div>
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="flex items-center gap-2">
        {getNodeTypeIcon()}
        <Input
          className="text-2xl font-bold tracking-tight w-full bg-transparent px-1 border-none shadow-none disabled:opacity-100"
          value={displayName}
          disabled={isInternalNode}
          onChange={(e) => updateNodeData("name", e.target.value)}
          placeholder="Enter node name"
        />
      </div>
      {/* Type specific content */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium pt-2">Type-Specific Properties</h3>
        {renderNodeTypeEditor()}
      </div>

      {/* General content */}
      <div className="space-y-4 border-t pt-2">
        <h3 className="text-lg font-medium">General Properties</h3>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Dependencies</Label>
            <div className="flex flex-wrap gap-2">
              {depData.length > 0 ? (
                depData.map(({ id, data }) => (
                  <Badge key={id} variant="secondary">
                    {data.displayName}
                  </Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No dependencies</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {node.dependencyIds.map((id) => (
                <Badge key={id} variant="secondary">
                  {editorNodes[id].displayName}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
